import { forwardRef } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, BoxProps } from '../Box';

/**
 * Vertical gradient colors for the screen background.
 * #FFFFFF (white) at top to #E7E7E7 (light gray) at bottom.
 */
const GRADIENT_STYLES: SxProps<Theme> = {
  background: 'linear-gradient(to bottom, #FFFFFF, #E7E7E7)',
  minHeight: '100dvh',
};

export type GradientBackgroundProps = Omit<BoxProps, 'ref'>;

/**
 * GradientBackground - Full-height wrapper with a subtle vertical gradient.
 *
 * Renders a container with a light vertical gradient (#FFFFFF → #E7E7E7) for use
 * behind screen content. Use on standalone screens (Settings, Error, no tenant)
 * so the main content sits on top of the gradient.
 *
 * **When to use:**
 * - Settings page
 * - Error page
 * - No tenant / empty state pages
 * - Any standalone screen that should have the shared gradient behind content
 *
 * @example
 * ```tsx
 * import { GradientBackground, PageContainer } from '@planitgeo/treeplotter-ui-library';
 *
 * function SettingsPage() {
 *   return (
 *     <GradientBackground>
 *       <PageContainer>
 *         <SettingsForm />
 *       </PageContainer>
 *     </GradientBackground>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With additional sx (e.g. flex layout)
 * <GradientBackground sx={{ display: 'flex', flexDirection: 'column' }}>
 *   <CenteredPageContainer>...</CenteredPageContainer>
 * </GradientBackground>
 * ```
 */
export const GradientBackground = forwardRef<HTMLDivElement, GradientBackgroundProps>(
  ({ sx, ...props }, ref) => {
    const mergedSx: SxProps<Theme> = sx ? [GRADIENT_STYLES, sx].flat() : GRADIENT_STYLES;
    return <Box ref={ref} sx={mergedSx} {...props} />;
  }
);

GradientBackground.displayName = 'GradientBackground';
